import { useFormTypedWithPartial } from "shared/app/LeadsTableView/hooks/useFormTypedWithPartial";
import { array, number, object, string } from "yup";
import { LeadType } from "views/leads-id/types";

export interface CreateLeadPayload {
  first_name: string;
  last_name: string;
  lead_type: LeadType;
  portfolio_size?: number | null;
  current_property_manager?: string | null;
  property_id?: number | null;
  unit_id?: number | null;
  primary_phone?: string;
  primary_email?: string;
  other_phones: string[];
  other_emails: string[];
  message: string;
  desired_move_in_date: string;
  source_id?: string;
  assignee_id?: number | null;
}

const PORTFOLIO_SIZE_MAX = 1_000_000;
export const CURRENT_PROPERTY_MANAGER_MAX = 255;

// Owner leads have no "Interested in" section, so only tenant leads can require it.
const interestedInField = (isRequired: boolean) =>
  isRequired
    ? string()
        .nullable()
        .when("lead_type", {
          is: (leadType?: string) => leadType !== LeadType.Owner,
          then: (schema) => schema.required("This field is required"),
          otherwise: (schema) => schema.optional()
        })
    : string().nullable().optional();

const buildValidationSchema = (requirePropertyAndUnit: boolean) =>
  object({
    first_name: string().required("This field is required"),
    last_name: string().required("This field is required"),
    primary_phone: string()
      .test(
        "phone-length",
        "Invalid phone number",
        (value) => !value || value.length === 10
      )
      .when("primary_email", {
        is: (email: string | undefined) => !email?.trim(),
        then: (schema) => schema.required("Phone or email is required")
      }),
    primary_email: string().optional(),
    other_phones: array().of(
      string()
        .required("This field is required")
        .length(10, "Invalid phone number")
    ),
    other_emails: array().of(
      string().required("This field is required").email("Invalid email address")
    ),
    desired_move_in_date: string().optional(),
    message: string().optional(),
    property_id: interestedInField(requirePropertyAndUnit),
    unit_id: interestedInField(requirePropertyAndUnit),
    source_id: string().when("lead_type", {
      is: (leadType?: string) => !leadType || leadType === LeadType.Tenant,
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.optional()
    }),
    assignee_id: string().nullable().optional(),
    lead_type: string()
      .oneOf([LeadType.Tenant, LeadType.Owner, LeadType.Realtor])
      .optional(),
    portfolio_size: number()
      .nullable()
      .optional()
      .integer("Must be a whole number")
      .min(0, "Must be 0 or more")
      .max(PORTFOLIO_SIZE_MAX, "Too large"),
    current_property_manager: string()
      .nullable()
      .optional()
      .max(CURRENT_PROPERTY_MANAGER_MAX, "Must be 255 characters or fewer")
  });

const VALIDATION_SCHEMA = buildValidationSchema(false);
const VALIDATION_SCHEMA_REQUIRED_PROPERTY_UNIT = buildValidationSchema(true);

const DEFAULT_VALUES: Partial<CreateLeadPayload> = {
  first_name: "",
  last_name: "",
  lead_type: LeadType.Tenant,
  portfolio_size: null,
  current_property_manager: "",
  source_id: "",
  primary_phone: "",
  primary_email: "",
  other_phones: [],
  other_emails: [],
  message: "",
  desired_move_in_date: "",
  assignee_id: null
};

export const useFormController = ({
  initialValues,
  requirePropertyAndUnit = false
}: {
  initialValues?: Partial<CreateLeadPayload> | CreateLeadPayload;
  requirePropertyAndUnit?: boolean;
} = {}) => {
  const {
    values,
    register,
    triggerPartialValidation,
    handleChange,
    onSubmit,
    onPartialSubmit,
    isSubmitting,
    reset,
    errors
  } = useFormTypedWithPartial<Partial<CreateLeadPayload> | CreateLeadPayload>(
    { ...DEFAULT_VALUES, ...(initialValues ?? {}) },
    requirePropertyAndUnit
      ? VALIDATION_SCHEMA_REQUIRED_PROPERTY_UNIT
      : VALIDATION_SCHEMA
  );

  const updatePrimaryPhone = (value: string) => {
    handleChange("primary_phone", () => value)();
  };

  const updatePrimaryEmail = (value: string) => {
    handleChange("primary_email", () => value)();
  };

  const addOtherPhone = () => {
    const newOtherPhones = [...(values.other_phones ?? []), ""];
    handleChange("other_phones", () => newOtherPhones)();
  };

  const updateOtherPhone = (index: number, value: string) => {
    const newOtherPhones = [...(values.other_phones ?? [])];
    newOtherPhones[index] = value;
    handleChange("other_phones", () => newOtherPhones)();
  };

  const removeOtherPhone = (index: number) => {
    const newOtherPhones = values.other_phones?.filter((_, i) => i !== index);
    handleChange("other_phones", () => newOtherPhones)();
  };

  const addOtherEmail = () => {
    const newOtherEmails = [...(values.other_emails ?? []), ""];
    handleChange("other_emails", () => newOtherEmails)();
  };

  const updateOtherEmail = (index: number, value: string) => {
    const newOtherEmails = [...(values.other_emails ?? [])];
    newOtherEmails[index] = value;
    handleChange("other_emails", () => newOtherEmails)();
  };

  const removeOtherEmail = (index: number) => {
    const newOtherEmails = values.other_emails?.filter((_, i) => i !== index);
    handleChange("other_emails", () => newOtherEmails)();
  };

  const setPropertyId = (id?: number | null) => {
    handleChange("property_id", () => id)();
  };
  const setUnitId = (id?: number | null) => {
    handleChange("unit_id", () => id)();
  };

  const setMessage = (message: string) => {
    handleChange("message", () => message)();
  };

  const setDesiredMoveInDate = (date: string) => {
    handleChange("desired_move_in_date", () => date)();
  };

  return {
    values,
    triggerPartialValidation,
    register,
    onSubmit,
    handleChange,
    onPartialSubmit,
    isSubmitting,
    reset,
    errors,
    updatePrimaryPhone,
    updatePrimaryEmail,
    addOtherPhone,
    updateOtherPhone,
    removeOtherPhone,
    addOtherEmail,
    updateOtherEmail,
    removeOtherEmail,
    setPropertyId,
    setUnitId,
    setMessage,
    setDesiredMoveInDate
  };
};
